/**
 * Campaign attribution off the widget's own URL — utm_* plus the Google click
 * ids (`gclid`, `gbraid`, `wbraid`).
 *
 * MIRROR of `ATTRIBUTION_SCHLUESSEL` in `@finavest/shared` (the widget bundle
 * stays zod-free); the two lists are compared in attribution.test.ts. The
 * immo-map widget carries the same capture (apps/immo-map/src/attribution.ts).
 *
 * On finavest.ch the calculator runs in an iframe, so `location.search` here is
 * the IFRAME's query: `public/embed.js` copies these keys from the host page
 * onto the iframe src. Standalone on tools.finavest.ch it is the landing URL.
 */
export const ATTRIBUTION_SCHLUESSEL = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'gbraid',
  'wbraid',
] as const;

/** The shared schema's per-value cap: a longer value would fail the whole submit. */
export const ATTRIBUTION_WERT_MAX = 200;

const KLICK_IDS: ReadonlySet<string> = new Set(['gclid', 'gbraid', 'wbraid']);

// MIRROR of the value rules in `@finavest/shared` (schemas/attribution.ts,
// RV616 m1/m5), compared value by value in attribution.test.ts: control
// characters out (one NUL made Postgres refuse the whole lead), a utm value
// trimmed and dropped when it looks like an e-mail address or a phone number,
// a click id only as a plain URL-safe token and never cleaned.
const KLICK_ID_FORMAT = /^[A-Za-z0-9_-]+$/;
const SIEHT_AUS_WIE_EMAIL = /[^\s@]@[^\s@]+\.[^\s@]{2,}/;
const SIEHT_AUS_WIE_TELEFON = /(?:^|\D)(?:\+|0)\d(?:[\s./()-]{0,2}\d){7,}/;

function istSteuerzeichen(c: number): boolean {
  return (
    c <= 0x1f ||
    (c >= 0x7f && c <= 0x9f) ||
    c === 0xad ||
    c === 0x061c ||
    c === 0x200b ||
    c === 0x200e ||
    c === 0x200f ||
    (c >= 0x2028 && c <= 0x202e) ||
    (c >= 0x2060 && c <= 0x2064) ||
    (c >= 0x2066 && c <= 0x2069) ||
    c === 0xfeff
  );
}

function ohneSteuerzeichen(wert: string): string {
  let aus = '';
  for (let i = 0; i < wert.length; i += 1) {
    const c = wert.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff) {
      const n = wert.charCodeAt(i + 1);
      if (n >= 0xdc00 && n <= 0xdfff) {
        aus += wert.charAt(i) + wert.charAt(i + 1);
        i += 1;
      }
      continue;
    }
    if ((c >= 0xdc00 && c <= 0xdfff) || istSteuerzeichen(c)) continue;
    aus += wert.charAt(i);
  }
  return aus;
}

export function bereinigeAttributionsWert(schluessel: string, wert: string): string | undefined {
  if (KLICK_IDS.has(schluessel)) return KLICK_ID_FORMAT.test(wert) ? wert : undefined;
  const s = ohneSteuerzeichen(wert).trim();
  if (!s || SIEHT_AUS_WIE_EMAIL.test(s) || SIEHT_AUS_WIE_TELEFON.test(s)) return undefined;
  return s;
}

/** A cut never ends on half a surrogate pair: Postgres refuses a lone one. */
function ganzeZeichen(s: string): string {
  const c = s.charCodeAt(s.length - 1);
  return c >= 0xd800 && c <= 0xdbff ? s.slice(0, -1) : s;
}

/**
 * A utm tag over the cap is cut (its prefix still names the campaign); a click
 * id over the cap is DROPPED — a truncated id matches no click, and a wrong id
 * in an offline import is worse than none. Every value is cleaned first (above).
 */
export function captureAttribution(search: string): Record<string, string> | undefined {
  const p = new URLSearchParams(search);
  const out: Record<string, string> = {};
  for (const k of ATTRIBUTION_SCHLUESSEL) {
    const roh = p.get(k);
    const v = roh === null ? undefined : bereinigeAttributionsWert(k, roh);
    if (!v) continue;
    if (v.length > ATTRIBUTION_WERT_MAX) {
      if (KLICK_IDS.has(k)) continue;
      out[k] = ganzeZeichen(v.slice(0, ATTRIBUTION_WERT_MAX));
    } else {
      out[k] = v;
    }
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * The visitor's ads choice (RV614 M3). A click id may only ride on a lead when
 * the ads choice was NOT refused, and the host page — not the widget — knows
 * that choice (its Consent Mode state). So right before a lead-minting submit
 * the widget asks its embedding page (`public/embed.js` answers from the
 * host's dataLayer). Asked at SUBMIT, not at load: a visitor who refuses in
 * the banner after the widget loaded must still be respected.
 *
 * Closed when in doubt: no answer within the timeout counts as refused. The
 * question is only asked when there IS a click id. A widget on its own page
 * (not framed, tools.finavest.ch) has no host to ask, and that page carries no
 * consent banner: nobody there could refuse, so no click id rides from it
 * either (RV616 m2). Its utm_* tags still do.
 */
export const CONSENT_QUERY = 'finavest:consent-query';
export const CONSENT_ANSWER = 'finavest:consent';
export const CONSENT_TIMEOUT_MS = 800;

export function ohneKlickIds(
  attr: Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (!attr) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(attr)) if (!KLICK_IDS.has(k)) out[k] = v;
  return Object.keys(out).length ? out : undefined;
}

export function adsGeweigertBijHost(timeoutMs = CONSENT_TIMEOUT_MS): Promise<boolean> {
  if (window.parent === window) return Promise.resolve(true);
  return new Promise((resolve) => {
    const luister = (e: MessageEvent): void => {
      const d = e.data as { type?: unknown; adsRefused?: unknown } | null;
      // SOURCE only, like armHostVisibility(): only our own embedding parent
      // can answer; `embed.js` checks origin AND source on its side.
      if (e.source !== window.parent || !d || d.type !== CONSENT_ANSWER) return;
      klaar(d.adsRefused !== false);
    };
    const wacht = setTimeout(() => klaar(true), timeoutMs);
    function klaar(geweigerd: boolean): void {
      clearTimeout(wacht);
      window.removeEventListener('message', luister);
      resolve(geweigerd);
    }
    window.addEventListener('message', luister);
    window.parent.postMessage({ type: CONSENT_QUERY }, '*');
  });
}

/** The attribution a lead-minting submit may carry: the URL's, minus click ids if refused. */
export async function attributionFuerSubmit(
  search: string,
  timeoutMs = CONSENT_TIMEOUT_MS,
): Promise<Record<string, string> | undefined> {
  const attr = captureAttribution(search);
  if (!attr || !Object.keys(attr).some((k) => KLICK_IDS.has(k))) return attr;
  return (await adsGeweigertBijHost(timeoutMs)) ? ohneKlickIds(attr) : attr;
}
